import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';
import { track, FEDERATION_PAGE } from '../utils/analytics';

const WORKFLOW = [
  'Define organization-level development objectives',
  'Organize programs and participating teams',
  'Establish reporting periods, templates, and accountability standards',
  'Review submissions, unresolved priorities, assigned actions, and program status',
  'Record technical-leadership decisions',
  'Produce organization-level development evidence and period reports',
];

const CAPABILITIES = [
  { title: 'Development objectives', text: 'Define organization-level technical-development priorities and connect program work to them.' },
  { title: 'Programs and participating teams', text: 'Organize technical programs and the teams participating in each one.' },
  { title: 'Standardized reporting templates', text: 'Establish consistent program-submission structures and evidence expectations.' },
  { title: 'Reporting periods', text: 'Configure quarterly, annual, or organization-specific reporting windows.' },
  { title: 'Assigned actions and ownership', text: 'Record responsibilities, owners, due dates, and completion status.' },
  { title: 'Cross-program oversight', text: 'Review unresolved priorities and execution status across authorized programs.' },
  { title: 'Leadership reviews', text: 'Record technical-leadership conclusions without automatically inferring improvement.' },
  { title: 'Institutional memory', text: 'Preserve objectives, decisions, actions, and reviews when staff change.' },
  { title: 'Development reports', text: 'Produce quarterly and annual evidence-supported organization reports.' },
  { title: 'Program-specific access controls', text: 'Scope staff access to the programs and participating teams they are authorized to review.' },
  { title: 'Audit history', text: 'Keep an organization-level history of important Federation changes and exports.' },
];

const PROGRAMS = [
  'Senior national teams',
  'Youth national teams',
  "Women's teams",
  'Academies',
  'Coach development programs',
];

const LEADERSHIP_QUESTIONS = [
  'Which development priorities remain unresolved?',
  'Which program submissions require leadership attention?',
  'Which assigned organizational actions were completed?',
  'Which programs are meeting their reporting responsibilities?',
  'Which technical-leadership decisions require follow-up?',
  'Where unresolved findings or incomplete actions concentrate across programs?',
];

const WORKSPACE_VIEWS = [
  { name: 'Dashboard', text: 'Summarizes program status, unresolved priorities, assigned actions, and review outcomes across programs you can access.' },
  { name: 'Programs', text: 'Organizes teams under federation programs with ownership and status for technical leadership.' },
  { name: 'Submissions', text: 'Collects standardized program updates for technical-leadership review.' },
  { name: 'Objectives', text: 'Defines development priorities that programs and findings can be aligned to over a reporting period.' },
  { name: 'Findings', text: 'Records organizational priorities from program submissions, leadership review, imports, or optional integrations.' },
  { name: 'Actions', text: 'Follows organizational actions from assignment through completion and leadership review.' },
  { name: 'Reviews', text: 'Records leadership assessments against prior findings and assigned actions.' },
  { name: 'Reports', text: 'Exports structured federation summaries for unresolved findings, action status, and period reviews.' },
  { name: 'Settings', text: 'Configures reporting periods, templates, and Federation workspace defaults for entitled organizations.' },
];

export default function ProductFederation() {
  usePageMeta(
    'Tactmark Federation | Technical Development Oversight for Football Associations',
    'Define development objectives, standardize program reporting, oversee execution, preserve institutional knowledge, and produce organization-level development evidence across football programs.'
  );

  useEffect(() => {
    track(FEDERATION_PAGE);
  }, []);

  const scrollToWorkflow = (event) => {
    event.preventDefault();
    const el = document.getElementById('federation-workflow');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-4xl mx-auto px-6 pt-16 pb-12 border-b border-gray-200">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">
          Tactmark Federation
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-6">
          Standardize and oversee football development across every program.
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mb-8">
          Tactmark Federation gives technical leadership one system to define development objectives, organize programs, establish reporting standards, oversee assigned actions and unresolved priorities, preserve institutional knowledge, and produce evidence-supported organization-level reports.
        </p>
        <div className="flex flex-wrap items-center gap-4 mb-10">
          <Link
            to="/contact?plan=federation&from=federation_product_page"
            className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors"
          >
            Book a Federation Demo
          </Link>
          <a
            href="#federation-workflow"
            onClick={scrollToWorkflow}
            className="border border-navy-900 text-navy-900 font-medium px-6 py-3 rounded hover:bg-navy-50 transition-colors"
          >
            Explore the Federation Workflow
          </a>
        </div>
      </div>

      <div className="bg-slate-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">What technical leadership gets</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
            One view across every development pathway.
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            Participating programs, submissions awaiting review, and coaching follow-through —
            so unresolved priorities do not disappear between reporting cycles.
          </p>
          <figure className="m-0">
            <img
              src="/product-proof/federation-desktop.png"
              alt="Tactmark Federation findings showing unresolved technical priorities across development pathways"
              className="w-full h-auto border border-gray-200 shadow-[0_18px_50px_rgba(18,33,64,0.08)]"
              width={1440}
              height={900}
            />
            <figcaption className="mt-3 text-sm text-gray-500">
              Tactmark Federation: unresolved findings and technical priorities across youth, senior, women’s, academy, and coach-development pathways.
            </figcaption>
          </figure>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-6">
          Technical development becomes difficult to oversee when objectives, submissions, actions, and decisions are distributed across documents and staff.
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
          Programs often use different reporting formats, assigned responsibilities are difficult to follow, and technical leadership must reconstruct development evidence manually. Tactmark Federation creates a consistent organization-level record.
        </p>
        <div className="border border-gray-200 rounded p-6 bg-gray-50">
          <p className="text-navy-900 font-semibold leading-relaxed">
            A federation needs to know which objectives programs are working toward, what was submitted, which priorities remain unresolved, who owns each action, and what leadership decided.
          </p>
        </div>
      </div>

      <div id="federation-workflow" className="bg-navy-900 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="text-navy-400 text-xs font-semibold tracking-widest uppercase mb-4">Federation workflow</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-10">
            A structured technical-development workflow.
          </h2>
          <ol className="space-y-5">
            {WORKFLOW.map((step, index) => (
              <li key={step} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded bg-white/10 text-white text-sm font-semibold flex items-center justify-center">
                  {index + 1}
                </span>
                <span className="text-navy-100 pt-1.5">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Federation capabilities</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-8">
          Tools built for organization-level technical development.
        </h2>
        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          {CAPABILITIES.map((item) => (
            <div key={item.title} className="border border-gray-200 rounded p-6">
              <h3 className="text-navy-900 font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        <h3 className="text-navy-900 font-semibold text-lg mb-3">Program segmentation</h3>
        <p className="text-gray-600 text-sm leading-relaxed mb-4 max-w-3xl">
          Allow authorized leadership to review programs when they exist in the organization. Not every football association uses all of these program types.
        </p>
        <div className="flex flex-wrap gap-3 mb-12">
          {PROGRAMS.map((item) => (
            <span key={item} className="border border-gray-200 text-navy-900 text-sm px-4 py-2 rounded">
              {item}
            </span>
          ))}
        </div>

        <h3 className="text-navy-900 font-semibold text-lg mb-3">Leadership views</h3>
        <p className="text-gray-600 text-sm leading-relaxed mb-4 max-w-3xl">
          Federation leadership can answer questions supported by program submissions, assigned responsibilities, leadership reviews, and documented evidence.
        </p>
        <ul className="space-y-2 text-gray-600 text-sm mb-12">
          {LEADERSHIP_QUESTIONS.map((q) => (
            <li key={q} className="flex gap-3">
              <span className="text-navy-400">-</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>

        <div className="mb-12">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Federation workspace</p>
          <h3 className="font-serif text-2xl text-navy-900 mb-3">
            Real views technical leadership uses day to day.
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-3xl">
            Federation is not a collage of demo dashboards. Entitled organizations work in these product views, each scoped to programs and teams they can access.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {WORKSPACE_VIEWS.map((view) => (
              <div key={view.name} className="border border-gray-200 rounded p-5">
                <h4 className="text-navy-900 font-semibold mb-1.5">{view.name}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{view.text}</p>
              </div>
            ))}
          </div>
          <Link
            to="/contact?plan=federation&from=federation_product_page"
            className="inline-flex bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors text-sm"
          >
            Book a Federation Demo
          </Link>
        </div>

        <div className="border border-gray-200 rounded p-6">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">Evidence discipline</p>
          <p className="text-gray-700 leading-relaxed">
            Federation conclusions are built from program submissions, leadership records, and submitted evidence. Tactmark distinguishes confirmed findings from priorities requiring further review. Post-Match may be connected later as an optional integration source, but it is not required.
          </p>
        </div>
      </div>

      <div className="border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
            Build a durable technical-development record across your organization.
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            Federation deployments are configured around participating teams, technical objectives, reporting requirements, and staff access.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact?plan=federation&from=federation_product_page"
              className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors"
            >
              Book a Federation Demo
            </Link>
            <Link to="/products" className="text-navy-900 font-medium px-2 py-3 hover:underline">View all products</Link>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
